import { Button, Modal, Row, Col, Form, Card, Spinner, Tabs, Tab, ListGroup } from "react-bootstrap";
import { FileText, CheckCircle, User, X, ArrowRight } from 'lucide-react'
import { useEffect, useState } from "react";
import Globals from "../../../Globals";
import InputCPF from "../../../components/InputCPF";
import api from "../../../api";
import Swal from "sweetalert2";
import useFetchDataPosConsult from "../hooks/useFetchDataPosConsult";

// Componente para o segundo step - seleção de exames
function ExamSelectionStep({ onPrevious, onComplete, selectedProcedures, setSelectedProcedures }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [procedures, setProcedures] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Estados para procedimentos secundários
  const [showSecondaryModal, setShowSecondaryModal] = useState(false);
  const [currentMainProcedure, setCurrentMainProcedure] = useState(null);
  const [secondarySearchTerm, setSecondarySearchTerm] = useState("");
  const [secondaryProcedures, setSecondaryProcedures] = useState([]);
  const [isSearchingSecondary, setIsSearchingSecondary] = useState(false);
  const [hasSearchedSecondary, setHasSearchedSecondary] = useState(false);

  // Busca de procedimentos
  useEffect(() => {
    const searchProcedures = async (term) => {
      if (term.length === 0) return;

      setIsSearching(true);
      setHasSearched(true);

      await api.get(`api/procedures/search/${encodeURIComponent(term)}`)
        .then(response => {
          setProcedures(response.data.data)
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          setIsSearching(false)
        })

    };

    const timer = setTimeout(() => {
      searchProcedures(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const searchSecondaryProcedures = async (term) => {
      if (term.length === 0) return;

      setIsSearchingSecondary(true);
      setHasSearchedSecondary(true);

      await api.get(`api/procedures/search/${encodeURIComponent(term)}`)
        .then(response => {
          setSecondaryProcedures(response.data.data)
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          setIsSearchingSecondary(false)
        })
    };

    const timer = setTimeout(() => {
      searchSecondaryProcedures(secondarySearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [secondarySearchTerm]);

  const handleProcedureToggle = (procedure) => {
    setSelectedProcedures(prev => {
      const isSelected = prev.some(p => p.id === procedure.id);
      if (isSelected) {
        return prev.filter(p => p.id !== procedure.id);
      } else {
        return [...prev, { ...procedure, secondaryProcedures: [] }];
      }
    });
  };

  const isProcedureSelected = (procedureId) => {
    return selectedProcedures.some(p => p.id === procedureId);
  };

  const openSecondaryModal = (procedure) => {
    setCurrentMainProcedure(procedure);
    setShowSecondaryModal(true);
    setSecondarySearchTerm("");
    setSecondaryProcedures([]);
    setHasSearchedSecondary(false);
  };

  const closeSecondaryModal = () => {
    setShowSecondaryModal(false);
    setCurrentMainProcedure(null);
    setSecondarySearchTerm("");
    setSecondaryProcedures([]);
    setHasSearchedSecondary(false);
  };

  const handleSecondaryToggle = (secondaryProcedure) => {
    setSelectedProcedures(prev => {
      return prev.map(p => {
        if (p.id === currentMainProcedure.id) {
          const secondaries = p.secondaryProcedures || [];
          const isSecondarySelected = secondaries.some(s => s.id === secondaryProcedure.id);

          if (isSecondarySelected) {
            return {
              ...p,
              secondaryProcedures: secondaries.filter(s => s.id !== secondaryProcedure.id)
            };
          } else {
            return {
              ...p,
              secondaryProcedures: [...secondaries, secondaryProcedure]
            };
          }
        }
        return p;
      });
    });
  };

  const isSecondarySelected = (secondaryId) => {
    if (!currentMainProcedure) return false;
    const mainProc = selectedProcedures.find(p => p.id === currentMainProcedure.id);
    if (!mainProc) return false;
    return (mainProc.secondaryProcedures || []).some(s => s.id === secondaryId);
  };

  const getSecondaryCount = (procedureId) => {
    const proc = selectedProcedures.find(p => p.id === procedureId);
    return proc?.secondaryProcedures?.length || 0;
  };

  return (
    <div className="exam-selection-step">
      {/* Campo de busca */}
      <Form.Group className="mb-4">
        <Form.Label>Buscar Procedimentos</Form.Label>
        <Form.Control
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
          placeholder="Busque por nome ou código cbhpm"
        />
      </Form.Group>

      {/* Área de resultados */}
      <div className="procedures-results mb-4" style={{ minHeight: '300px', maxHeight: '400px', overflowY: 'auto' }}>
        {!hasSearched && searchTerm.length < 3 && (
          <div className="text-center p-5 text-muted">
            <FileText size={48} className="mb-3" />
            <p className="fs-5">Busque procedimentos no campo acima</p>
          </div>
        )}

        {isSearching && (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">Buscando procedimentos...</p>
          </div>
        )}

        {hasSearched && !isSearching && procedures.length === 0 && (
          <div className="text-center p-5 text-muted">
            <FileText size={48} className="mb-3" />
            <p className="fs-5">Nenhum procedimento encontrado</p>
            <p className="small">Tente usar outros termos de busca</p>
          </div>
        )}

        {hasSearched && !isSearching && procedures.length > 0 && (
          <div className="row g-3">
            {procedures.map((procedure) => (
              <div key={procedure.id} className="col-12">
                <Card
                  className={`procedure-card ${isProcedureSelected(procedure.id) ? 'border-success' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleProcedureToggle(procedure)}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-start">
                      <Form.Check
                        type="checkbox"
                        checked={isProcedureSelected(procedure.id)}
                        onChange={() => handleProcedureToggle(procedure)}
                        className="me-3 mt-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-grow-1">
                        <h6 className="fw-semibold mb-1">{procedure.name}</h6>
                        <p className="text-muted small mb-1">Código: {procedure.cbppm_code || procedure.own_code}</p>
                        {isProcedureSelected(procedure.id) && getSecondaryCount(procedure.id) > 0 && (
                          <p className="text-success small mb-0">
                            {getSecondaryCount(procedure.id)} secundário(s) adicionado(s)
                          </p>
                        )}
                      </div>
                      {isProcedureSelected(procedure.id) && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSecondaryModal(procedure);
                          }}
                        >
                          + Secundário (s)
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal show={showSecondaryModal} onHide={closeSecondaryModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Adicionar Procedimentos Secundários
            {currentMainProcedure && (
              <div className="text-muted small mt-2">
                Para: {currentMainProcedure.name}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label>Buscar Procedimentos Secundários</Form.Label>
            <Form.Control
              type="text"
              value={secondarySearchTerm}
              onChange={(e) => setSecondarySearchTerm(e.target.value.toUpperCase())}
              placeholder="Busque por nome ou código cbhpm"
            />
          </Form.Group>

          <div style={{ minHeight: '300px', maxHeight: '400px', overflowY: 'auto' }}>
            {!hasSearchedSecondary && secondarySearchTerm.length < 3 && (
              <div className="text-center p-5 text-muted">
                <FileText size={48} className="mb-3" />
                <p className="fs-5">Busque procedimentos secundários</p>
              </div>
            )}

            {isSearchingSecondary && (
              <div className="text-center p-5">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <p className="text-muted">Buscando procedimentos...</p>
              </div>
            )}

            {hasSearchedSecondary && !isSearchingSecondary && secondaryProcedures.length === 0 && secondarySearchTerm.length >= 3 && (
              <div className="text-center p-5 text-muted">
                <FileText size={48} className="mb-3" />
                <p className="fs-5">Nenhum procedimento encontrado</p>
                <p className="small">Tente usar outros termos de busca</p>
              </div>
            )}

            {hasSearchedSecondary && !isSearchingSecondary && secondaryProcedures.length > 0 && (
              <div className="row g-3">
                {secondaryProcedures.map((secProcedure) => (
                  <div key={secProcedure.id} className="col-12">
                    <Card
                      className={`procedure-card ${isSecondarySelected(secProcedure.id) ? 'border-success' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSecondaryToggle(secProcedure)}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex align-items-start">
                          <Form.Check
                            type="checkbox"
                            checked={isSecondarySelected(secProcedure.id)}
                            onChange={() => handleSecondaryToggle(secProcedure)}
                            className="me-3 mt-1"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-grow-1">
                            <h6 className="fw-semibold mb-1">{secProcedure.name}</h6>
                            <p className="text-muted small mb-0">Código: {secProcedure.cbppm_code || secProcedure.own_code}</p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSecondaryModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>




      <div className="d-flex gap-2 pt-4">
        <Button variant="outline-secondary" className="flex-grow-1" onClick={onPrevious}>
          Voltar
        </Button>
        <Button
          type="submit"
          variant="success"
          className="flex-grow-1"
          onClick={onComplete}
          disabled={selectedProcedures.length === 0}
        >
          <CheckCircle size={24} className="me-2" /> Salvar Dados
        </Button>
      </div>
    </div>
  );
}

export default function PatientInfoModal({ patient, isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [shopper, setShopper] = useState(null);
  const [formError, setFormError] = useState("");

  // Tabs
  const [activeTab, setActiveTab] = useState("pdf");
  const [selectedProcedures, setSelectedProcedures] = useState([]);

  // Estados do paciente
  const [patientCpf, setPatientCpf] = useState("");
  const [patientBirth, setPatientBirth] = useState("");
  const [fullName, setFullName] = useState("");
  const [fullNameDisabled, setFullNameDisabled] = useState(true);
  const [patientFound, setPatientFound] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Estados do profissional
  const [professionalName, setProfessionalName] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorSuggestions, setDoctorSuggestions] = useState([]);
  const [showDoctorSuggestions, setShowDoctorSuggestions] = useState(false);
  const [isSearchingDoctor, setIsSearchingDoctor] = useState(false);

  const {
    refetch: refetchPatientsAll,
  } = useFetchDataPosConsult(
    "/api/clinic-procedures/patient-summary-all-day-new",
    {
      date: new Date().toISOString().split("T")[0]
    }
  );

  useEffect(() => {
    if (isOpen && patient) {
      loadPdfFile();
      getShopper();
      checkProfessional();
    }
  }, [isOpen, patient]);

  const loadPdfFile = async () => {
    setLoadingFile(true);

    try {
      const response = await api.get(`api/client/budgets/new/${patient.id}`);
      const path = response.data.data.image_path;

      if(path.startsWith('https')){
        setPdfFile(`${path}#navpanes=0`)
      } else {
        setPdfFile(`${Globals.BASE_URL}/storage/${path}#navpanes=0`)
      }

    } catch (error) {
      console.error(error);
    } finally {
      setLoadingFile(false);
    }
  };

  const getShopper = async () => {
    await api.get('api/shoppers/14')
      .then(response => {
        setShopper(response.data.data)
      })
  }

  const checkProfessional = () => {
    if (patient.patient && patient.patient.professional_cpf) {

      setSelectedDoctor({
        cpf: patient.patient.professional_cpf,
        name: patient.patient.professional_name,
      })
    }
  }

  const resetModalState = () => {
    setCurrentStep(1);
    setPdfFile(null);
    setPatientCpf("");
    setPatientBirth("");
    setFullName("");
    setFullNameDisabled(true);
    setPatientFound(false);
    setSelectedPatient(null);
    setProfessionalName("");
    setSelectedDoctor(null);
    setDoctorSuggestions([]);
    setShowDoctorSuggestions(false);
    setSelectedProcedures([]);
    setActiveTab("pdf");
  };

  const handleClose = () => {
    resetModalState();
    onClose();
  };

  // Buscar paciente por CPF e data de nascimento
  useEffect(() => {
    const searchCustomer = async (cpf, birth) => {
      const onlyDigits = (str) => str.replace(/\D/g, '');
      const customerCpf = onlyDigits(cpf);

      const year = birth.split('-')[0];
      const yearHasLeadingZero = year.length === 4 && year.startsWith('0')

      if (customerCpf.length < 11 || birth.length === 0 || yearHasLeadingZero) return;

      setLoading(true)

      try {
        const response = await api.post('api/customers/search/cpf/birth', {
          cpf: customerCpf,
          birth
        });

        if (response.data.data.length !== 0) {
          setPatientFound(true);
          setSelectedPatient(response.data.data);
          setFullName(response.data.data.name);
        } else {
          setPatientFound(false);
          setFullName('');
        }

        setFullNameDisabled(false);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    const timer = setTimeout(() => {
      searchCustomer(patientCpf, patientBirth);
    }, 500);

    return () => clearTimeout(timer);
  }, [patientCpf, patientBirth]);

  // Buscar médicos
  useEffect(() => {
    const searchDoctors = async (name) => {
      if (name.length < 3) {
        setDoctorSuggestions([]);
        setShowDoctorSuggestions(false);
        return;
      }

      setIsSearchingDoctor(true);
      setShowDoctorSuggestions(true);

      try {
        const response = await api.get(`api/professionals/search/${encodeURIComponent(name)}`);

        if (response.data && response.data.data) {
          setDoctorSuggestions(response.data.data);
          setShowDoctorSuggestions(true);
        } else {
          setDoctorSuggestions([]);
          setShowDoctorSuggestions(false);
        }
      } catch (error) {
        console.error('Erro ao buscar médicos:', error);
        setDoctorSuggestions([]);
        setShowDoctorSuggestions(false);
      } finally {
        setIsSearchingDoctor(false);
      }
    };

    const timer = setTimeout(() => {
      searchDoctors(professionalName);
    }, 300);

    return () => clearTimeout(timer);
  }, [professionalName]);

  const selectDoctor = (doctor) => {
    setProfessionalName(doctor.name);
    setSelectedDoctor(doctor);
    setShowDoctorSuggestions(false);
    setDoctorSuggestions([]);
  };

  const removeSelectedDoctor = () => {
    setSelectedDoctor(null);
    setDoctorSuggestions([]);
    setProfessionalName("");
  };

  const handleDoctorInputBlur = () => {
    setTimeout(() => {
      setShowDoctorSuggestions(false);
    }, 200);
  };

  const handleNextStep = () => {
    if (!patientCpf || !patientBirth || !fullName || !selectedDoctor) {
      setFormError("Preencha todas as informações para avançar");
      return;
    }

    setFormError(""); // limpa erro se estiver tudo ok
    setCurrentStep(2);
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const saveSolicitationInfos = async (e) => {
    e.preventDefault();

    const result = await Swal.fire({
      icon: 'question',
      title: 'Tem certeza?',
      text: 'Deseja realmente salvar as informações da solicitação?',
      showCancelButton: true,
      confirmButtonText: 'Sim, salvar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    // Se não confirmou, sai da função
    if (!result.isConfirmed) {
      return;
    }

    Swal.fire({
      icon: 'info',
      title: 'Aguarde...',
      text: 'Salvando dados',
      allowOutsideClick: false,
      showConfirmButton: false,
      showCancelButton: false,
      showCloseButton: false
    })

    let currentPatient = selectedPatient;

    if (!patientFound) {
      await api.post('api/customers', {
        cpf: patientCpf.replaceAll(".", "").replaceAll("-", ""),
        name: fullName,
        birth: patientBirth,
        mobile: patient.name.replaceAll(' ', '').replaceAll('(', '').replaceAll(')', '').replaceAll('-', ''),
        dependent: false
      })
        .then((response => {
          currentPatient = response.data.data;
          setSelectedPatient(currentPatient);
        }))
    }

    await api.put(`api/client/budgets/${patient.id}`, {
      status: 'closed'
    })
      .then(async (response) => {
        await api.post('api/clinic-procedures/external/loose', {
          shopper: {
            access_key: shopper.access_key
          },
          clinic_id: 0,
          scheduling_id: null,
          customer_cpf: patientCpf.replaceAll(".", "").replaceAll("-", ""),
          customer_birth: patientBirth,
          professional_cpf: selectedDoctor.cpf,
          requisition_id: null,
          procedures: selectedProcedures.map(procedure => ({
            ...procedure,
            procedure_id: procedure.id,
            clinic_id: 0,
            quantity: null,
            secondary: procedure.secondaryProcedures ? procedure.secondaryProcedures.map(sp => ({
              ...sp,
              procedure_id: sp.id,
              clinic_id: 0,
              quantity: null,
            })) : []
          })),
          erase_procedures: false
        })

        Swal.close();

        //se nao for o dia de hoje:
        // diz que precisa mudar pra hoje
        // ou mudar a data pra hoje (uma funcao no pai que seta a data pra hoje)

        window.dispatchEvent(new CustomEvent('updateDateAndRefetch'));

        Swal.fire({
          icon: 'success',
          title: 'Solicitação salva com sucesso!',
          allowOutsideClick: false,
        })
      })
      .catch(error => {
        console.error('Error updating attendance:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Ocorreu um erro ao salvar as informações do atendimento.'
        });
      });

    handleClose();
  };

  return (
    <Modal show={isOpen} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {currentStep === 1 ? 'Informações do atendimento' : 'Seleção de Exames'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="gy-4" style={{ overflowY: 'auto' }}>
          <Col lg={6}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="pdf" title="Arquivo da solicitação">
                <div className="border rounded bg-light p-3 h-100 d-flex justify-content-center align-items-center">
                  {loadingFile ? (
                    <div className="text-center">
                      <Spinner animation="border" variant="primary" className="mb-3" />
                      <div className="h5 fw-semibold">Carregando arquivo...</div>
                      <div className="text-muted small">
                        Aguarde enquanto o PDF está sendo carregado.
                      </div>
                    </div>
                  ) : pdfFile ? (
                    <div className="w-100 h-100" style={{ minHeight: '500px' }}>
                      <iframe
                        src={pdfFile}
                        width="100%"
                        height="100%"
                        style={{ border: 'none', minHeight: '500px' }}
                        title="Visualização do arquivo da solicitação"
                      />
                    </div>
                  ) : (
                    <div className="text-center text-muted">
                      <FileText size={28} />
                      <div className="h5 fw-semibold">PDF não disponível</div>
                      <div className="text-muted small">
                        Nenhum arquivo PDF foi encontrado para este atendimento.
                      </div>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="procedures" title={`Procedimentos Selecionados (${selectedProcedures.reduce((total, p) => total + 1 + (p.secondaryProcedures?.length || 0), 0)})`}>
                {selectedProcedures.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    Nenhum procedimento selecionado.
                  </div>
                ) : (
                  <div className="bg-light p-2 rounded" style={{ maxHeight: "500px", overflowY: "auto" }}>
                    <ListGroup>
                      {selectedProcedures.map((p) => (
                        <div key={p.id}>
                          <ListGroup.Item className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex flex-column">
                              <span className="fw-semibold">{p.name}</span>
                              <span className="text-muted small">Código: {p.cbhpm_code || p.own_code}</span>
                              {p.secondaryProcedures && p.secondaryProcedures.length > 0 && (
                                <span className="text-success small mt-1">
                                  {p.secondaryProcedures.length} secundário(s)
                                </span>
                              )}
                            </div>

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                setSelectedProcedures((prev) => prev.filter((sp) => sp.id !== p.id))
                              }
                            >
                              <X size={16} />
                            </Button>
                          </ListGroup.Item>

                          {p.secondaryProcedures && p.secondaryProcedures.length > 0 && (
                            <div className="ms-4 mb-2">
                              {p.secondaryProcedures.map((sec) => (
                                <ListGroup.Item
                                  key={sec.id}
                                  className="d-flex justify-content-between align-items-center mb-1 bg-light border-start border-3 border-success"
                                >
                                  <div className="d-flex flex-column">
                                    <span className="fw-semibold small">↳ {sec.name}</span>
                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                      Código: {sec.cbhpm_code || sec.own_code}
                                    </span>
                                  </div>

                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedProcedures((prev) =>
                                        prev.map((proc) =>
                                          proc.id === p.id
                                            ? {
                                              ...proc,
                                              secondaryProcedures: proc.secondaryProcedures.filter(
                                                (s) => s.id !== sec.id
                                              ),
                                            }
                                            : proc
                                        )
                                      );
                                    }}
                                  >
                                    <X size={14} />
                                  </Button>
                                </ListGroup.Item>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </ListGroup>
                  </div>
                )}
              </Tab>
            </Tabs>
          </Col>

          {currentStep === 1 ? (
            <Col lg={6}>
              <h5 className="fw-semibold">Informações do paciente</h5>

              <Form onSubmit={saveSolicitationInfos}>
                <div className="mb-3 d-flex">
                  <Form.Group className="me-5 col-5">
                    <Form.Label>CPF</Form.Label>
                    <InputCPF
                      value={patientCpf}
                      onChange={setPatientCpf}
                    />
                  </Form.Group>

                  <Form.Group className="col-6">
                    <Form.Label>Data de nascimento</Form.Label>
                    <Form.Control
                      type="date"
                      value={patientBirth}
                      onChange={(e) => setPatientBirth(e.target.value)}
                      required
                    />
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Nome completo</Form.Label>
                  <Form.Control
                    value={loading ? 'BUSCANDO PACIENTE. AGUARDE ...' : fullName}
                    onChange={(e) => setFullName(e.target.value.toUpperCase())}
                    placeholder="Nome completo do paciente"
                    required
                    disabled={fullNameDisabled || patientFound}
                    className="customer-name-input"
                  />
                </Form.Group>

                {patientFound && (
                  <span className="text-gray">
                    <CheckCircle size={30} className="me-2 text-success" />
                    Paciente já cadastrado
                  </span>
                )}

                <hr className="my-4" />

                <h5 className="fw-semibold">Informações do profissional</h5>

                <Form.Group className="mb-4">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    value={professionalName}
                    onChange={(e) => setProfessionalName(e.target.value)}
                    onFocus={() => setShowDoctorSuggestions(doctorSuggestions.length > 0)}
                    onBlur={handleDoctorInputBlur}
                    placeholder="Buscar nome do profissional"
                    disabled={selectedDoctor !== null}
                    required
                  />

                  {(showDoctorSuggestions && !selectedDoctor) && (
                    <div
                      className="position-absolute bg-white border rounded shadow-sm mt-1"
                      style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto', width: '40%' }}
                    >
                      {isSearchingDoctor && (
                        <div className="p-2 text-muted text-center">
                          <small>Buscando médicos...</small>
                        </div>
                      )}

                      {!isSearchingDoctor && doctorSuggestions.length === 0 && (
                        <div className="p-2 text-muted text-center">
                          <small>Nenhum médico encontrado</small>
                        </div>
                      )}

                      {!isSearchingDoctor && doctorSuggestions.map((doctor, index) => (
                        <div
                          key={index}
                          className="p-2 border-bottom cursor-pointer hover-bg-light"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => selectDoctor(doctor)}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          <div className="fw-semibold">{doctor.name}</div>
                          <small className="text-muted">
                            {doctor.council_name} - {doctor.council_state}: {doctor.council_number}
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>

                {selectedDoctor && (
                  <div className="mb-4">
                    <Card className="border-success">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-circle bg-success d-flex align-items-center justify-content-center text-white me-3"
                              style={{ width: '40px', height: '40px' }}
                            >
                              <User size={24} />
                            </div>
                            <div>
                              <div className="fw-semibold text-success mb-1">
                                <CheckCircle size={24} className="me-2" />
                                Profissional Selecionado
                              </div>
                              <div className="fw-bold">{selectedDoctor.name}</div>
                              {
                                (selectedDoctor.council_name && selectedDoctor.council_state && selectedDoctor.council_number) && (
                                  <div className="text-muted small">
                                    {selectedDoctor.council_name} - {selectedDoctor.council_state}: {selectedDoctor.council_number}
                                  </div>
                                )
                              }
                            </div>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={removeSelectedDoctor}
                            title="Remover profissional selecionado"
                          >
                            <X size={24} />
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                )}

                <div className="d-flex flex-column gap-2 pt-3">
                  <div className="d-flex gap-2">
                    <Button variant="outline-secondary" className="flex-grow-1" onClick={handleClose}>
                      Cancelar
                    </Button>
                    <Button className="flex-grow-1" onClick={handleNextStep} disabled={loadingFile || loading}>
                      <ArrowRight size={24} className="me-2" /> Selecionar Exames
                    </Button>
                  </div>

                  {formError && (
                    <div className="text-danger small mt-2">{formError}</div>
                  )}
                </div>

              </Form>
            </Col>
          ) : (
            <Col lg={6}>
              <ExamSelectionStep
                onPrevious={handlePreviousStep}
                onComplete={saveSolicitationInfos}
                selectedProcedures={selectedProcedures}
                setSelectedProcedures={setSelectedProcedures}
              />
            </Col>
          )}
        </Row>
      </Modal.Body>
    </Modal>
  );
}
